<script setup lang="ts">
import { type FrontContent, type RelatedContent } from "@gasparigit/omnia-sdk";
import { DateFormatter } from "@internationalized/date";
import { google, ics, office365, outlook } from "calendar-link";
import { AREAS_MAP } from "~/lib/const";
import { createEvent, getContentType, link } from "~/lib/utils/contents";
import { processError } from "~/lib/utils/errors";
import { defineSeo } from "~/lib/utils/seo";
import type { CreateLinkConfig } from "~/utils/types";

const omniaSdk = useOmniaSdk();

defineProps<{
  linkConfig: CreateLinkConfig;
}>();

const content = defineModel<FrontContent>("content", { required: true });

const imageData = computed(() => {
  if (!content || !(content.value.contentable_type === "notizia" || content.value.contentable_type === "link" || content.value.contentable_type === "postit")) return null;
  let full = false;

  if (content.value.contentable_type === "notizia" && content.value.news_type === "videonota") {
    if (content.value.video) return null;
    if (content.value.video_thumb_url) return { src: content.value.video_thumb_url, full: true };
  }

  if (content.value.contentable_type === "link" && content.value.link_type === "corso-formazione") full = true;
  if (content.value.contentable_type === "postit") full = true;

  return { src: content.value.image?.original_url, srcset: content.value.image?.srcset, full };
});

defineSeo(content.value);

async function saveContent() {
  try {
    const savedContent = await omniaSdk.v1.contents.save(content.value.id);

    content.value.is_saved = savedContent.is_saved;
  } catch (error) {
    processError(error);
  }
}

const df = new DateFormatter("it-IT", {
  dateStyle: "long",
});

const relatedContents = computed(() => {
  if (!content.value) return [];

  let related = [...content.value.related_contents, ...content.value.related_contents_inverse];
  return related.filter((r: RelatedContent, i: number) => related.findIndex((c: RelatedContent) => c.id === r.id) === i);
});

const relatedContentsInherited = computed(() => {
  if (!content.value?.related_contents?.length) return [];

  return content.value.related_contents.filter((content: RelatedContent) => content.inherit_permissions);
});

const relatedContentsNotInherited = computed(() => {
  if (!relatedContents.value?.length) return [];

  return relatedContents.value.filter((content: RelatedContent) => !content.inherit_permissions);
});

const hasAttachments = computed(() => {
  if (!content.value.files?.length) return false;

  for (const file of content.value.files) {
    if (file.file) return true;
  }

  return false;
});

const articoliNorma = computed(() => {
  if (content.value.contentable_type !== "norma" || !content.value?.articoli?.length) return null;
  return content.value.articoli;
  // const seen = new Map<string, boolean>();

  // return content.value.articoli.map((articolo) => {
  //   const key = `${articolo.abstract || ""}_${articolo.number || ""}`;

  //   if (!seen.has(key)) {
  //     seen.set(key, true);
  //     return articolo;
  //   }

  //   return {
  //     ...articolo,
  //     abstract: null,
  //   };
  // });
});

function print() {
  window.print();
}
</script>

<template>
  <div class="main-content mb-10 print:mb-2">
    <div class="lg:grid grid-cols-4 gap-10 xl:gap-20">
      <div class="col-span-3 mb-16 lg:mb-0">
        <div class="md:flex items-end justify-between mb-4">
          <div class="mb-3 md:mb-0">
            <div class="info-label">{{ getContentType(content) }}</div>
            <p v-if="content.contentable_type === 'modello' || content.contentable_type === 'pratica'" class="info-number mt-3">
              {{ content.contentable_type === "pratica" ? "Pratica " : "" }}Cod. {{ content.code }}
            </p>
            <template
              v-else-if="
                content.contentable_type === 'scadenza' ||
                content.contentable_type === 'scadenza-elettorale' ||
                content.contentable_type === 'scadenzario-elettorale' ||
                content.contentable_type === 'articolo-rivista'
              "
            ></template>
            <template v-else-if="content.contentable_type === 'link' || content.contentable_type === 'norma'">
              <span v-if="content.publisher" class="chip chip-solid-gray inline-block mt-3">
                {{ content.publisher.title }}
              </span>
              <span v-if="content.contentable_type === 'link' && content.link_type === 'gazzetta' && content.date && content.number" class="info-number mt-3">
                Gazzetta n. {{ content.number }} del {{ df.format(content.date) }}
              </span>
            </template>
            <p v-else-if="content.contentable_type === 'quesito' && content.date" class="info-number mt-3">Quesito del {{ df.format(content.date) }}</p>
            <p v-else class="info-number mt-3">{{ df.format(content.published_at || content.created_at) }}</p>
          </div>
          <div class="flex items-center gap-3 print:hidden">
            <button
              v-if="['modello', 'scadenzario-elettorale'].indexOf(content.contentable_type) < 0"
              @click="print"
              class="flex items-center text-base font-semibold text-themeGray hover:text-primary transition-colors"
            >
              <Icon name="carbon:printer" class="w-5 h-5 me-1" />
              <span>Stampa</span>
            </button>
            <DropdownMenu v-if="content.contentable_type === 'scadenza' || content.contentable_type === 'scadenza-elettorale'">
              <DropdownMenuTrigger as-child>
                <Button variant="link" class="flex items-center text-base font-semibold text-themeGray hover:text-primary transition-colors">
                  <Icon name="carbon:calendar-add" class="w-5 h-5 me-1" />
                  <span>Aggiungi al calendario</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                <DropdownMenuGroup>
                  <DropdownMenuItem><NuxtLink target="_blank" class="w-full" :href="google(createEvent(content))">Google</NuxtLink></DropdownMenuItem>
                  <DropdownMenuItem><NuxtLink target="_blank" class="w-full" :href="outlook(createEvent(content))">Outlook</NuxtLink></DropdownMenuItem>
                  <DropdownMenuItem><NuxtLink target="_blank" class="w-full" :href="office365(createEvent(content))">Office 365</NuxtLink></DropdownMenuItem>
                  <DropdownMenuItem><NuxtLink target="_blank" class="w-full" :href="ics(createEvent(content))">File ICS (Apple)</NuxtLink></DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <button @click="saveContent" class="flex items-center text-base font-semibold text-themeGray hover:text-primary transition-colors">
              <IconsBookmark :filled="content.is_saved" class="w-5 h-5 me-1" />
              <span>Aggiungi ai preferiti</span>
            </button>
          </div>
        </div>

        <!-- QUI CI SONO IMMAGINI, SCADENZA O NUMERO PER LE INFO CHE LO PREVEDONO -->
        <div v-if="content.contentable_type === 'scadenza' || content.contentable_type === 'scadenza-elettorale'" class="font-monospace text-tertiary text-2xl print:text-xl">
          <p class="font-semibold">{{ content.date?.toLocaleDateString("it-IT", { weekday: "long" }) }}</p>
          <div class="flex items-center gap-3">
            <p class="text-7xl print:text-5xl font-bold">{{ content.date?.getDate() }}</p>
            <p class="leading-tight">
              <span>{{ content.date?.toLocaleString("it-IT", { month: "long" }) }}</span>
              <span class="block">{{ content.date?.getFullYear() }}</span>
            </p>
          </div>
        </div>
        <div class="text-muted-foreground flex flex-col mt-3" v-if="content.contentable_type === 'scadenza-elettorale'">
          <div>
            <span v-html="content.string_relative_to_poster_date"></span>
          </div>
          <div>
            <span v-html="content.string_relative_to_election_date"></span>
          </div>
        </div>

        <!-- IMMAGINI/VIDEO -->
        <div v-if="content.contentable_type === 'articolo-rivista' && content.capitolo?.numero_rivista" class="font-monospace flex items-center text-xl gap-3 mb-4">
          <p class="text-6xl text-primary font-semibold">{{ content.capitolo.numero_rivista.number }}</p>
          <p class="leading-tight" v-if="content.capitolo.numero_rivista.published_at">
            <span>{{ content.capitolo.numero_rivista.published_at.toLocaleString("default", { month: "long" }) }}</span>
            <span class="block">{{ content.capitolo.numero_rivista.published_at.getFullYear() }}</span>
          </p>
        </div>
        <div class="mb-10 flex items-center justify-center print:hidden">
          <img loading="lazy" v-if="imageData" :src="imageData.src" :srcset="imageData.srcset" :class="['max-h-[450px] object-cover', imageData.full && 'w-full']" />
          <template v-if="content.contentable_type === 'notizia'">
            <template v-if="content.video || content.video_url">
              <ClientOnly>
                <VideoPlayer
                  :options="{
                    controls: true,
                    poster: content.video_url || !content.video_url ? undefined : content.video_thumb_url,
                    sources: [content.video_url ? { src: content.video_url, type: '' } : { src: content.video.download_url, type: content.video.mime_type }],
                    aspectRatio: '16:9',
                  }"
                ></VideoPlayer>
              </ClientOnly>
            </template>
          </template>
        </div>

        <!-- TITOLO DELLA RIVISTA -->
        <template v-if="content.contentable_type === 'articolo-rivista' && content.capitolo?.numero_rivista?.rivista">
          <NuxtLink :href="link({ rivista: content.capitolo?.numero_rivista.rivista, numeroRivista: content.capitolo?.numero_rivista })" class="text-primary text-2xl font-semibold mt-8 -mb-2">
            {{ content.capitolo.numero_rivista.rivista.title }}
          </NuxtLink>
        </template>

        <!-- TITOLO -->
        <h2 class="text-4xl font-extrabold mt-5 print:text-2xl">{{ content.title }}</h2>

        <!-- SOTTOTIOLO E AUTORE IN CASO DI ARTICOLO DI RIVISTA -->
        <p v-if="content.contentable_type === 'articolo-rivista' && content.author" class="text-themeGray mt-3">
          <span class="font-thin">(A cura di {{ content.author }})</span>
        </p>

        <!-- LINK AL MEMOWEB -->
        <p v-if="content.contentable_type === 'notizia' && content?.memowebs?.length && content?.memowebs?.at(0)" class="mt-3">
          Dal <NuxtLink :href="link({ memoweb: content.memowebs.at(0) })" title="Vai al Memoweb" class="link">{{ content.memowebs.at(0)?.title }}</NuxtLink>
        </p>

        <!-- NUMERO GAZZETTA -->
        <div v-if="content.contentable_type === 'link' && content.link_type === 'gazzetta' && content.number" class="flex items-center gap-3 mt-3">
          <span class="font-semibold">Gazzetta Ufficiale n.</span>
          <div class="min-w-14 h-14 p-2 flex items-center justify-center font-monospace font-bold bg-primary rounded-full text-white mt-1">{{ content.number }}</div>
        </div>

        <div v-if="'front_url' in content && content.front_url">
          <a class="link inline-block mt-3" :href="content.front_url" target="_blank" rel="noopener noreferrer">{{ content.front_url }}</a>
        </div>

        <div class="mt-8 print:mt-4">
          <!-- RIASSUNTO -->
          <div
            class="mb-8 print:mb-6 prose max-w-full font-semibold text-lg print:text-base print:italic print:font-normal prose-a:text-primary"
            v-if="content.abstract?.length"
            v-html="content.abstract?.replace(/\n/g, '<br>')"
          ></div>

          <!-- TESTO -->
          <FrontContentBodyWithFiles :text="content.body" />
          <FrontUnauthorized class="mb-10" v-if="!content.allowed" :link-config="{ ...linkConfig, content }" />

          <!-- QUI GLI ARTICOLI IN CASO DI NORMATIVA -->
          <div v-if="articoliNorma" class="mb-8">
            <div class="mt-5" v-for="articolo in articoliNorma">
              <h3 class="text-2xl">
                <NuxtLink :href="link({ ...linkConfig, content: articolo })" class="link font-semibold">{{ articolo.title }}</NuxtLink>
              </h3>
              <p>{{ articolo.abstract }}</p>
            </div>
          </div>

          <!-- MODELLI -->
          <div v-if="content.contentable_type === 'pratica' && content.modelli?.length" class="text-base bg-primary-50 p-3 mb-8">
            <div class="flex flex-col gap-y-3 divide-y">
              <template v-for="modello in content.modelli" :key="`pratica-${content.id}-modello-${modello.id}`">
                <FrontContentItemsModello v-if="modello.code" :link-config="linkConfig" :content="modello" />
                <span v-else class="font-bold text-lg pt-3">{{ modello.title }}</span>
              </template>
            </div>
          </div>

          <!-- ALLEGATI -->
          <div class="border rounded-sm shadow-md px-5 py-6 mb-8 print:shadow-none print:px-2 print:py-4" v-if="content.contentable_type !== 'modello' && hasAttachments">
            <template v-for="attachment in content.files">
              <FrontDownloadButton :content="content" :file="attachment" :title="attachment.title" class="flex items-center mb-5 last:mb-0" v-if="attachment.file">
                <IconsAttach class="w-7 h-7 me-1" />
                <div class="link font-semibold flex gap-2">
                  <span>{{ attachment.title }}</span>
                  <span class="uppercase whitespace-nowrap">({{ attachment.file.human_readable_size }})</span>
                </div>
              </FrontDownloadButton>
            </template>
          </div>

          <!-- MODULISTCA -->
          <ClientOnly>
            <div class="border rounded-sm shadow-md px-5 py-6 mb-8" v-if="content.contentable_type === 'modello' && !content.is_demo && hasAttachments">
              <FrontDownloadModel v-model="content" />
            </div>
            <div v-if="content.contentable_type === 'modello' && content.is_demo && content.files?.length">
              <FrontPdfViewer :content="content" :file="content.files[0]" />
            </div>
          </ClientOnly>

          <FrontSideCard title="Contenuti allegati" v-if="relatedContentsInherited?.length">
            <FrontContentItem v-for="related in relatedContentsInherited" :link-config="{}" :content="(related as unknown as FrontContent)" :compact="true" />
          </FrontSideCard>
        </div>
      </div>

      <!-- HUB E CORRELATI -->
      <div class="col-span-1 space-y-10 print:hidden">
        <FrontSideCard title="Scadenzario elettorale" theme="tertiary" v-if="content.contentable_type === 'scadenza-elettorale' && content.scadenzario_elettorale">
          <h3 class="font-semibold text-xl border-b last:border-none pb-4 last:pb-0">
            <NuxtLink :to="link({ ...linkConfig, content: content.scadenzario_elettorale })" :title="content.scadenzario_elettorale.title" class="hover:text-primary-600 transition-colors">
              {{ content.scadenzario_elettorale.title }}
            </NuxtLink>
          </h3>
        </FrontSideCard>

        <FrontSideCard title="Pratiche" theme="tertiary" v-if="content.contentable_type === 'modello' && content.pratiche?.length">
          <h3 class="font-semibold text-xl border-b last:border-none pb-4 last:pb-0" v-for="pratica in content.pratiche">
            <NuxtLink :to="link({ content: pratica })" :title="pratica.title" class="hover:text-primary-600 transition-colors">{{ pratica.title }}</NuxtLink>
          </h3>
        </FrontSideCard>

        <FrontSideCard title="Sezioni dedicate all'argomento" theme="tertiary" v-if="content.hubs?.length && content.show_hubs">
          <h3 class="font-semibold text-xl border-b last:border-none pb-4 last:pb-0" v-for="hub in content.hubs">
            <NuxtLink :to="link({ hub })" :title="hub.title" class="hover:text-primary-600 transition-colors">{{ hub.title }}</NuxtLink>
          </h3>
        </FrontSideCard>

        <FrontSideCard title="Contenuti correlati" v-if="relatedContentsNotInherited?.length">
          <FrontContentItem v-for="related in relatedContentsNotInherited" :link-config="{}" :content="(related as unknown as FrontContent)" :compact="true" />
        </FrontSideCard>
      </div>
    </div>
  </div>

  <FrontScadenzarioElettorale v-model:content="content" v-if="content.contentable_type === 'scadenzario-elettorale'" />

  <!-- AREE -->
  <div class="main-content flex flex-wrap gap-x-2 font-semibold text-base pt-2 mt-auto print:mt-0 mb-10">
    <span v-for="area in content.areas" :class="['text-' + AREAS_MAP[area.id].color + '-foreground']">{{ area.title }}</span>
  </div>

  <AdminLinkToAdmin class="print:hidden" :config="{ content }" />
</template>
